import * as Mutations from './mutations';
import { CURRENCY_LIST } from '@/constants/currencies';
import { PaginationParams } from '@/common/utils/pagination-params';
import { TransactionsService } from '@/common/services/pages/transactions.service';
import { SchoolService } from '@/common/services/settings/school.service';


export const TransactionsModule = {
    state: {
        transactions: null,
        transaction: null,
        chargeId: null,
        transactionTotalAmount: null,
        paynowTransactionFeature: null,
        paynowTransactionPaginationParams: {
            from: 0,
            to: 10,
            page: 0,
            isPrevDisabled: true,
            isNextDisabled: false,
            searchText: '',
            prevSearchText: ''
        },
        paynowTransactionPageSize: 10,
    },

    mutations: {
        [Mutations.SET_TRANSACTIONS] (state, transactions) {
            state.transactions = transactions;
        },

        [Mutations.SET_TRANSACTION] (state, transaction) {
            state.transaction = transaction;
        },

        [Mutations.SAVE_CHARGE_ID] (state, chargeId) {
            state.chargeId = chargeId;
        },

        [Mutations.CLEAR_CHARGE_ID] (state) {
            state.chargeId = null;
        },

        [Mutations.SET_TRANSACTIONS_TOTAL_AMOUNT] (state, totalAmount) {
            state.transactionTotalAmount = totalAmount;
        },

        [Mutations.CLEAR_TRANSACTIONS_TOTAL_AMOUNT] (state) {
            state.transactionTotalAmount = null;
        },

        [Mutations.SET_PAYNOW_TRANSACTION_FEATURE] (state, feature) {
            state.paynowTransactionFeature = feature;
        },
        [Mutations.SET_PAYNOW_TRANSACTION_PAGINATION_PARAMS] (state, paginationParams) {
            state.paynowTransactionPaginationParams = paginationParams;
        },
        [Mutations.SET_PAYNOW_TRANSACTION_PAGE_SIZE] (state, pageSize) {
            state.paynowTransactionPageSize = pageSize;
        },
    },

    actions: {
        updatePaynowTransactionPageSize(context, pageSize) {
            context.commit(Mutations.SET_PAYNOW_TRANSACTION_PAGE_SIZE, pageSize);
        },
        toggleOldTransaction(context) {
            const isPaynowTransactionFeature = context.state.paynowTransactionFeature && context.state.paynowTransactionFeature.enabled;
            context.commit(Mutations.SET_PAYNOW_TRANSACTION_FEATURE, { enabled: !isPaynowTransactionFeature });
        },
        resetOldTransaction(context) {
            const isNewPaymentFeature = context.rootGetters.schoolInfo && context.rootGetters.schoolInfo.features && context.rootGetters.schoolInfo.features["new-payments"];
            if(isNewPaymentFeature) {
                context.commit(Mutations.SET_PAYNOW_TRANSACTION_FEATURE, { enabled: true });
            }
        },

        async getTransactions(context, page = 0) {
            if (page < 0)  return;
            
            // check if paynow feature is enabled
            let isPaynowFeature = false;
            if (!context.state.paynowTransactionFeature) {
                const { schoolId, userId } = context.getters;

                const { data } = await SchoolService.getProfileInfo(schoolId, userId);

                if (data.features && data.features["new-payments"]) { // check if paynow feature is enabled
                    context.commit(Mutations.SET_PAYNOW_TRANSACTION_FEATURE, { enabled: true });
                } else {
                    context.commit(Mutations.SET_PAYNOW_TRANSACTION_FEATURE, { enabled: false });
                }
            } else {
                isPaynowFeature = context.state.paynowTransactionFeature.enabled;
            }

            // if paynow feature is enabled, fetch transactions using paynow endpoint
            if (context.state.paynowTransactionFeature && context.state.paynowTransactionFeature.enabled) {
            
                
                let paginationParams = {
                    size: context.getters.paynowTransactionPageSize,
                    page: page,
                    isPrevDisabled: page === 0,
                    isNextDisabled: false,
                    searchText: context.getters.searchQuery,
                    status: context.getters.filters && context.getters.filters.invoiceStatus,
                }



                let response = await TransactionsService.initInvoiceForNewPaymentFlow(context.getters.schoolId, mapPaginationParamsToQueryString(paginationParams));
   
                const transactions = response && mapTransactionFromNewFlowToOld(response.data);
                
                context.commit(Mutations.SET_PAYNOW_TRANSACTION_PAGINATION_PARAMS, {
                    ...paginationParams,
                    isNextDisabled: transactions.length < context.getters.paynowTransactionPageSize,
                });
                const scrollContinerClass = 'page-layout';
                const scrollContainer = document.querySelector(`.${scrollContinerClass}`);   
                if (scrollContainer) {
                    scrollContainer.scrollTo({
                        top: 0,
                        behavior: 'smooth'
                    }); 
                }
                context.commit(Mutations.SET_TRANSACTIONS, transactions);
                context.dispatch('configPagination', {
                    callback: () => {
                        return context.dispatch('getTransactions');
                }
                });

            } else {
                const { data: { content, pageable, totalPages, totalElements, numberOfElements } } =
                await TransactionsService.getTransactions(context.getters.schoolId,
                    PaginationParams.createParams({
                        size: context.getters.pageSize,
                        page: context.getters.pageNumber,
                        sort: context.getters.sortBy,
                        direction: context.getters.direction,
                        query: context.getters.searchQuery,
                        filters: {
                            ...context.getters.filters
                        }
                    }));
                context.commit(Mutations.SET_TRANSACTIONS, content);
                context.dispatch('configPagination', {
                    ...pageable,
                    numberOfElements,
                    totalElements,
                    totalPages,
                    callback: () => {
                        return context.dispatch('getTransactions');
                }
                });
            }
            context.dispatch('resetSelectedItems');
        },

        openChargeModal(context, payload) {
            const { chargeId, removeChild, amount = 0, rosterId, activityType  } = payload;
            context.commit(Mutations.SAVE_CHARGE_ID, chargeId);
            context.dispatch('getTransactionTotalAmount', {chargeId, amount, rosterId, activityType });
            context.dispatch('openModal', {
                modalName: 'RefundModal',
                options: { removeChild, rosterId, activityType, chargeId },
                callback: () => {
                    removeChild && removeChild();
                    context.commit(Mutations.CLEAR_CHARGE_ID);
                    context.commit(Mutations.CLEAR_TRANSACTIONS_TOTAL_AMOUNT);
                }
            });
        },

        async refundCharge(context, data) {          
            const isNewPaymentFeature = context.rootGetters.schoolInfo && context.rootGetters.schoolInfo.features && context.rootGetters.schoolInfo.features["new-payments"];
            const { schoolId, chargeId } = context.getters;
            try {
                context.dispatch('showSpinner');
                const form = { ...data, chargeId: chargeId || data.chargeId };
                if (isNewPaymentFeature || (context.state.paynowTransactionFeature && context.state.paynowTransactionFeature.enabled)) {
                   // new payment flow
                   if (form.activityType && form.rosterId) {
                    await TransactionsService.paynowActivityRefundCharge(form);
                   } else {
                    await TransactionsService.paynowRefundCharge(schoolId, form);
                   }
                   const page = context.getters.transactionsPage;                
                   _.isNil(context.getters.options.removeChild) &&  await context.dispatch('getTransactions', page);        
                   setTimeout(() => {
                    context.dispatch('createSuccessNotification', 'You have successfully created a refund');
                   }, 500)
                } else {
                    await TransactionsService.refundCharge(schoolId, {
                        chargeId: chargeId || data.chargeId,
                        amount: data.amount,
                        reason: data.reason
                    });
                    _.isNil(context.getters.options.removeChild) &&  await context.dispatch('getTransactions');
                    context.dispatch('createSuccessNotification', 'You have successfully created a refund');
                }
            } catch (error) {
                throw error;
            } finally {
                context.dispatch('hideSpinner');
            }
        },

        async transactionResendReceipts(context, data) {
            const { schoolId } = context.getters;
            _.isArray(data) && data.length
                ? await TransactionsService.transactionBulkResendReceipts(schoolId, data)
                : await TransactionsService.transactionSingleResendReceipts(schoolId, data);
            _.isArray(data) && context.dispatch('resetSelectedItems');
            context.dispatch('createSuccessNotification', _.isArray(data) ? 'You have successfully resend receipts' : 'You have successfully resend receipt');
        },

        async previewTransaction(context, chargeId) {
            context.commit(Mutations.SAVE_CHARGE_ID, chargeId);
            await context.dispatch('getTransaction');
            context.dispatch('openModal', {
                modalName: 'InvoicePreviewModal',
                callback: () => {
                    context.commit(Mutations.CLEAR_CHARGE_ID);
                    context.commit(Mutations.SET_TRANSACTION, null)
                },
                options: {
                    width: '680px',
                    info: context.getters.transaction
                }
            });
        },

        async getTransaction(context) {
            let { data } = await TransactionsService.getTransaction(context.getters.schoolId, context.getters.chargeId);

            data = {
                ...data,
                download: false,
                currency: data && CURRENCY_LIST.find(currency => currency.short === _.upperCase(data.currency)).long
            };

            context.commit(Mutations.SET_TRANSACTION, data);
        },

        async getTransactionTotalAmount(context, payload) {
            const { chargeId, amount , rosterId, activityType} = payload;
            if (context.state.paynowTransactionFeature && context.state.paynowTransactionFeature.enabled) {
                if (rosterId && activityType) {
                    const { data } = await TransactionsService.paynowActivityRefundTotalAmount( rosterId, activityType );
                    context.commit(Mutations.SET_TRANSACTIONS_TOTAL_AMOUNT, data.refundAmountAvailable || 0);
                } else {
                    context.commit(Mutations.SET_TRANSACTIONS_TOTAL_AMOUNT, amount);
                }
                
            } else {
                try{
                const { data } = await TransactionsService.getTransactionTotalAmount(context.getters.schoolId, chargeId);
                context.commit(Mutations.SET_TRANSACTIONS_TOTAL_AMOUNT, data);
                }
                catch(e){
                    console.log(e)
                }
            }
        }
    },

    getters: {
        transactions: state => state.transactions,

        transaction: state => state.transaction,

        chargeId: state => state.chargeId,

        transactionTotalAmount: state => state.transactionTotalAmount,

        paynowTransactionFeature: state => state.paynowTransactionFeature && state.paynowTransactionFeature.enabled,

        isPaynowPrevPageDisabled: state => state.paynowTransactionPaginationParams.isPrevDisabled,

        isPaynowNextPageDisabled: state => state.paynowTransactionPaginationParams.isNextDisabled,

        transactionsPage: state => state.paynowTransactionPaginationParams.page,

        paynowTransactionPageSize: state => state.paynowTransactionPageSize,

    }
};

export function mapTransactionFromNewFlowToOld(transaction) { 
    return transaction.map(transaction => {
        return {
            chargeId: transaction.id,
            createdAt: transaction.date,
            parentName: transaction.parent.name,
            parentEmail: transaction.parent.email,
            activityName: transaction.activityDetails.name,
            childName: transaction.child.name,
            amount: transaction.status === 'paid' ? transaction.amountPaid : transaction.amountDue,
            refund: transaction.refundAmount,
            uniqueId: transaction.id,
            due: transaction.dueDate,
            status: transaction.status,
            paymentUrl: transaction.paymentUrl,
            downloadUrl: transaction.downloadUrl,
            activityId: transaction.activityDetails && transaction.activityDetails.id,
        }
    });
}

/**
 * Converts pagination parameters object to a URL query string
 * @param {Object} paginationParams - The pagination parameters object
 * @returns {String} The formatted query string
 */
function mapPaginationParamsToQueryString(paginationParams) {
    // Create an array to hold the query parameters
    const queryParams = [];
    
    // Only include relevant properties that should appear in the URL
    const includedProps = ['size', 'page', 'searchText', 'status'];
    
    // Add each valid property to the queryParams array
    for (const prop of includedProps) {
      if (paginationParams[prop] !== undefined && 
          paginationParams[prop] !== null && 
          paginationParams[prop] !== '') {
        queryParams.push(`${prop}=${encodeURIComponent(paginationParams[prop])}`);
      }
    }
    
    // Join all parameters with "&" and prefix with "?"
    return queryParams.length ? `?${queryParams.join('&')}` : '';
  }


// WEBPACK FOOTER //
// ./src/store/modules/transactions/transactions.module.js